import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, get, post, type RundownDto, type RundownItemDto } from '../api.js';
import { PageTitle } from '../frame.js';
import { t } from '../i18n.js';
import { Link } from '../router.js';
import { useSession } from '../session.js';
import { classes, fmtDateInput, hhmmss, mmss } from '../util.js';
import { realtime, type WsMessage } from '../ws.js';
import { GraphicsDialog } from './graphics-dialog.js';
import { MediaPanel } from './media-panel.js';
import { PublishDialog } from './rundown-dialogs.js';
import type { SyncInfo } from './rundown-grid.js';

interface Player {
  id: string;
  name: string;
  enabled: boolean;
  mode: string | null;
  state: string | null;
}
interface Health {
  worker: { playout: { adapter: string; online: boolean; lastEventAt: string | null; pending: number; failed: number; lagMs: number | null } } | null;
}

/**
 * Έλεγχος playout: the producer's console for one bulletin. Link or publish the rundown to a player,
 * then drag rows into their running order: every move, cut or clip change goes through the same
 * mutation the editorial grid uses, and the relay applies it to the player at once (the on-air and
 * cued rows are protected by the relay and shown here). Clip assignment sits on the right.
 */
export function PlayoutControlPage({ id }: { id: string }) {
  const { can } = useSession();
  const [rundown, setRundown] = useState<RundownDto | null>(null);
  const [sync, setSync] = useState<SyncInfo>({ links: [], items: [] });
  const [players, setPlayers] = useState<Player[]>([]);
  const [voPlayer, setVoPlayer] = useState<string | null>(null);
  const [health, setHealth] = useState<Health | null>(null);
  const [cursor, setCursor] = useState(0);
  const [drag, setDrag] = useState<{ id: string; over: string | null; after: boolean } | null>(null);
  const [publish, setPublish] = useState(false);
  const [graphicsFor, setGraphicsFor] = useState<RundownItemDto | null>(null);
  const [graphicsVersion, setGraphicsVersion] = useState(0);
  const [mediaTitles, setMediaTitles] = useState<Map<string, string>>(new Map());
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await get<{ rundown: RundownDto; sync: SyncInfo }>(`/api/rundowns/${id}`);
      setRundown(r.rundown);
      setSync(r.sync);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, [id]);

  const onMessage = useCallback(
    (m: WsMessage) => {
      if (m.type === 'rundown' && m.rundownId === id) setRundown(m.view as RundownDto);
      if (m.type === 'sync' && m.rundownId === id) setSync({ links: m.links as SyncInfo['links'], items: m.items as SyncInfo['items'] });
    },
    [id],
  );
  useEffect(() => {
    const unsub = realtime.subscribe(id);
    const off = realtime.listen(onMessage);
    const initial = window.setTimeout(() => void load(), 0);
    return () => {
      window.clearTimeout(initial);
      unsub();
      off();
    };
  }, [id, load, onMessage]);

  // players and the relay's health, every five seconds
  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      get<{ players: Player[]; voPlayer: string | null }>('/api/integrations/players')
        .then((r) => {
          if (cancelled) return;
          setPlayers(r.players);
          setVoPlayer(r.voPlayer);
        })
        .catch(() => undefined);
      get<Health>('/api/integrations/health')
        .then((r) => !cancelled && setHealth(r))
        .catch(() => undefined);
    };
    tick();
    const h = window.setInterval(tick, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(h);
    };
  }, []);

  const items = useMemo(() => rundown?.items ?? [], [rundown]);
  const mediaIdsKey = items
    .map((it) => it.mediaAssetId)
    .filter(Boolean)
    .sort()
    .join(',');
  useEffect(() => {
    if (!mediaIdsKey) return;
    let cancelled = false;
    get<{ media: Array<{ id: string; title: string }> }>(`/api/media?ids=${encodeURIComponent(mediaIdsKey)}&limit=200`)
      .then((r) => !cancelled && setMediaTitles(new Map(r.media.map((m) => [m.id, m.title]))))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [mediaIdsKey]);

  const playoutState = useMemo(() => new Map(sync.items.filter((s) => s.target === 'playout').map((s) => [s.itemId, s])), [sync]);
  const link = sync.links.find((l) => l.target === 'playout');

  const mutate = useCallback(
    async (mutations: Array<Record<string, unknown>>): Promise<boolean> => {
      if (!rundown) return false;
      try {
        const r = await post<{ rundown: RundownDto }>(`/api/rundowns/${id}/mutations`, { baseRevision: rundown.revision, mutationId: crypto.randomUUID(), mutations });
        setRundown(r.rundown);
        setNotice(null);
        return true;
      } catch (e) {
        if (e instanceof ApiError && e.status === 409) {
          setNotice(t('conflict'));
          await load();
          return false;
        }
        setError(e instanceof ApiError ? e.message : String(e));
        return false;
      }
    },
    [id, rundown, load],
  );

  const move = (itemId: string, toIndex: number) => mutate([{ op: 'move', itemId, toIndex }]);
  const onDrop = async () => {
    if (!drag || !drag.over || drag.over === drag.id) return setDrag(null);
    const from = items.findIndex((i) => i.id === drag.id);
    const overIdx = items.findIndex((i) => i.id === drag.over);
    let to = overIdx + (drag.after ? 1 : 0);
    if (from < to) to -= 1;
    setDrag(null);
    if (from !== to) await move(drag.id, to);
  };
  const onKey = async (e: React.KeyboardEvent<HTMLDivElement>) => {
    const it = items[cursor];
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (e.altKey && it && can('rundown.reorder')) await move(it.id, Math.min(items.length - 1, cursor + 1)).then((ok) => ok && setCursor((c) => Math.min(items.length - 1, c + 1)));
      else setCursor((c) => Math.min(items.length - 1, c + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (e.altKey && it && can('rundown.reorder')) await move(it.id, Math.max(0, cursor - 1)).then((ok) => ok && setCursor((c) => Math.max(0, c - 1)));
      else setCursor((c) => Math.max(0, c - 1));
    }
  };

  if (error && !rundown) return <div className="banner err">{error}</div>;
  if (!rundown) return <div>{t('loading')}</div>;
  const badge = (s: string) => <span className={`status ${s === 'synced' ? 'ok' : s === 'pending' ? 'warn' : s === 'blocked' || s === 'failed' ? 'err' : 'info'}`}>{t(s as 'pending')}</span>;
  const playout = health?.worker?.playout;
  const onAir = items.find((it) => playoutState.get(it.id)?.downstreamStatus === 'playing');
  const next = items.find((it) => playoutState.get(it.id)?.downstreamStatus === 'cued');

  return (
    <>
      <PageTitle title={`${t('controlTitle')} ${rundown.programmeName} ${fmtDateInput(rundown.date)}`} hint={t('controlHint')} />
      <div className="control-bar">
        <div className="control-link">
          <b>Playout:</b>{' '}
          {link ? (
            <>
              {badge(link.state)} <span className="muted">{link.targetRef}</span>
              {link.lastLatencyMs !== null && <span className="muted"> · {link.lastLatencyMs} ms</span>}
              {link.lastError && <span className="status err"> {link.lastError}</span>}
            </>
          ) : (
            <span className="status info">{t('unlinked')}</span>
          )}
          {playout && (
            <span className="muted">
              {' '}
              · {t('relay')} {playout.online ? <span className="status ok">online</span> : <span className="status err">offline</span>} · {t('pending')} {playout.pending} · {t('failed')} {playout.failed}
            </span>
          )}
        </div>
        <div className="toolbar">
          <Link className="btn" to={`/rundown/${id}`}>
            ☰ {t('gridTitle')}
          </Link>
          <Link className="btn" to={`/control/${id}`} title={t('crPickHint')}>
            📺 {t('crTitle')}
          </Link>
          <button className="btn" onClick={() => void load()}>
            ↻ {t('refresh')}
          </button>
          <button className="btn primary" onClick={() => setPublish(true)} disabled={!can('rundown.publish')}>
            ⇧ {link ? t('publishManage') : t('publishLink')}
          </button>
        </div>
      </div>
      {notice && (
        <div className="banner warn">
          {notice}
          <button className="btn" onClick={() => setNotice(null)}>
            OK
          </button>
        </div>
      )}
      {error && <div className="banner err">{error}</div>}
      <div className="control-now">
        <div className="control-now-cell onair">
          <span className="label">{t('onAir')}</span>
          <span className="value">{onAir ? `${onAir.displayNumber ?? ''} ${onAir.title}` : '—'}</span>
        </div>
        <div className="control-now-cell next">
          <span className="label">{t('nextUp')}</span>
          <span className="value">{next ? `${next.displayNumber ?? ''} ${next.title}` : '—'}</span>
        </div>
        <div className="control-now-cell">
          <span className="label">{t('bulletinTime')}</span>
          <span className="value">{hhmmss(rundown.totalS)}</span>
        </div>
      </div>
      <div className="grid-with-panel">
        <div className="control-list grid-scroll" tabIndex={0} onKeyDown={(e) => void onKey(e)} style={{ outline: 'none' }} onDragOver={(e) => e.preventDefault()} onDrop={() => void onDrop()}>
          {items.map((it, idx) => {
            const ps = playoutState.get(it.id);
            return (
              <div
                key={it.id}
                className={classes('control-row', idx === cursor && 'cursor', it.floated && 'floated', it.kind === 'separator' && 'separator', drag?.id === it.id && 'dragging', drag?.over === it.id && (drag.after ? 'drop-after' : 'drop-before'), ps?.downstreamStatus === 'playing' && 'onair', ps?.downstreamStatus === 'cued' && 'next')}
                draggable={can('rundown.reorder')}
                onDragStart={(e) => {
                  setDrag({ id: it.id, over: null, after: false });
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  const rect = e.currentTarget.getBoundingClientRect();
                  const after = e.clientY > rect.top + rect.height / 2;
                  setDrag((d) => (d && (d.over !== it.id || d.after !== after) ? { ...d, over: it.id, after } : d));
                }}
                onDragEnd={() => setDrag(null)}
                onClick={() => setCursor(idx)}
              >
                <span className="handle" title={t('dragToReorder')} aria-hidden="true">
                  ⋮⋮
                </span>
                <span className="order">{it.kind === 'separator' ? '' : it.floated ? '⟂' : it.order}</span>
                <span className="num">{it.kind === 'separator' ? '' : (it.displayNumber ?? '-')}</span>
                <span className="title">
                  {it.kind === 'separator' ? (it.title !== '.' ? it.title : '') : it.title}
                  {it.kind !== 'separator' && <span className="kind muted"> {it.kind}</span>}
                </span>
                <span className="clip" title={it.mediaAssetId ?? ''}>
                  {it.mediaAssetId ? (
                    <>
                      {it.mediaVo && <span className="vo-tag">VO</span>}🎬 {mediaTitles.get(it.mediaAssetId) ?? it.mediaAssetId}
                    </>
                  ) : it.mediaCode ? (
                    <span className="virtual" title={t('virtualWaiting')}>
                      ⌛ {it.mediaCode}
                    </span>
                  ) : it.storyId ? (
                    <span className="muted">{t('noClip')}</span>
                  ) : (
                    ''
                  )}
                </span>
                <span className="dur">{it.kind === 'separator' ? '' : mmss(it.totalS)}</span>
                <span className="state">
                  {ps ? (
                    <span className={`status ${ps.state === 'synced' ? 'ok' : ps.state === 'pending' ? 'warn' : 'err'}`} title={ps.blockedReason ?? ''}>
                      {ps.downstreamStatus ?? ps.state}
                    </span>
                  ) : link ? (
                    <span className="status info">—</span>
                  ) : null}
                </span>
                <span className="row-actions">
                  <button type="button" title={t('moveUp')} disabled={!can('rundown.reorder') || idx === 0} onClick={(e) => (e.stopPropagation(), void move(it.id, idx - 1))}>
                    ▲
                  </button>
                  <button type="button" title={t('moveDown')} disabled={!can('rundown.reorder') || idx === items.length - 1} onClick={(e) => (e.stopPropagation(), void move(it.id, idx + 1))}>
                    ▼
                  </button>
                </span>
              </div>
            );
          })}
          {items.length === 0 && <p className="muted">—</p>}
        </div>
        <div className="control-side">
          <div className="panel players">
            <div className="panel-head">
              <span className="panel-title">{t('playoutPlayer')}</span>
            </div>
            {players.length === 0 && <p className="muted small" style={{ padding: '4px 10px' }}>{t('noPlayersYet')}</p>}
            {players.map((p) => (
              <div key={p.id} className="player-row">
                <b>{p.name}</b> <span className="muted">{p.mode ?? ''}</span>{' '}
                <span className={`status ${p.state === 'playing' ? 'ok' : p.enabled ? 'info' : 'warn'}`}>{p.state ?? (p.enabled ? 'idle' : 'off')}</span>
                {link?.targetRef === p.id && <span className="status ok"> {t('linkedHere')}</span>}
                {voPlayer === p.id && <span className="status info"> {t('voPlayerTag')}</span>}
              </div>
            ))}
          </div>
          <MediaPanel
            item={items[cursor] ?? null}
            canEdit={can('story.edit.own')}
            onAssign={async (mediaAssetId) => {
              const it = items[cursor];
              if (!it) return false;
              return mutate([{ op: 'update', itemId: it.id, patch: { mediaAssetId, mediaInMs: null, mediaOutMs: null } }]);
            }}
            onGraphics={() => items[cursor] && setGraphicsFor(items[cursor]!)}
            graphicsVersion={graphicsVersion}
            onClose={() => undefined}
          />
        </div>
      </div>
      {publish && <PublishDialog rundown={rundown} sync={sync} onClose={() => setPublish(false)} onChanged={() => void load()} />}
      {graphicsFor && (
        <GraphicsDialog
          item={graphicsFor}
          onClose={() => {
            setGraphicsFor(null);
            setGraphicsVersion((v) => v + 1);
          }}
        />
      )}
    </>
  );
}
